// @figma file=pkCGby54zXzTsccXvHqzO6 node=15783:324591 name="Form" set="mytaible-shadcn-studio" verified=2026-03-21

import { Slot } from '@radix-ui/react-slot';
import * as React from 'react';
import {
  Controller,
  type ControllerProps,
  type ControllerRenderProps,
  type FieldPath,
  type FieldValues,
  FormProvider,
  useFormContext,
} from 'react-hook-form';
import { cn } from '../../../lib/utils';

// ─── FormFieldContext ──────────────────────────────────────────────────────────

interface FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  name: TName;
}

const FormFieldContext = React.createContext<FormFieldContextValue>({} as FormFieldContextValue);

// ─── FormItemContext ───────────────────────────────────────────────────────────

interface FormItemContextValue {
  id: string;
}

const FormItemContext = React.createContext<FormItemContextValue>({} as FormItemContextValue);

// ─── useFormField ──────────────────────────────────────────────────────────────

/**
 * Hook that reads the current field state from the nearest `FormField` +
 * `FormItem` context. Returns stable IDs for label / description / message
 * wiring and the react-hook-form `FieldState`.
 *
 * Must be used inside: `<Form> → <FormField> → <FormItem>`.
 */
function useFormField() {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const { getFieldState, formState } = useFormContext();

  if (!fieldContext.name) {
    throw new Error('useFormField must be used within a <FormField>');
  }

  const fieldState = getFieldState(fieldContext.name, formState);
  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  };
}

// ─── Form ─────────────────────────────────────────────────────────────────────

/**
 * Root form provider. Wraps react-hook-form's `FormProvider` to supply form
 * state to all nested sub-components.
 *
 * @example
 * const form = useForm<z.infer<typeof schema>>({ resolver: zodResolver(schema) });
 * <Form {...form}>
 *   <form onSubmit={form.handleSubmit(onSubmit)}>
 *     <FormField control={form.control} name="username" render={...} />
 *   </form>
 * </Form>
 */
const Form = FormProvider;

// ─── FormField ────────────────────────────────────────────────────────────────

export interface FormFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends Omit<ControllerProps<TFieldValues, TName>, 'render'> {
  /**
   * Render prop — receives `{ field }` from react-hook-form `Controller`.
   * Pass `field` directly to the input primitive (e.g. `<Input {...field} />`).
   */
  render: (props: { field: ControllerRenderProps<TFieldValues, TName> }) => React.ReactElement;
}

/**
 * Binds a react-hook-form `Controller` to the `FormFieldContext` so that
 * `FormLabel`, `FormControl`, `FormDescription`, and `FormMessage` can read
 * the field name and validation state.
 *
 * @example
 * <FormField
 *   control={form.control}
 *   name="email"
 *   render={({ field }) => (
 *     <FormItem>
 *       <FormLabel>Email</FormLabel>
 *       <FormControl><Input type="email" {...field} /></FormControl>
 *       <FormDescription>We will never share your email.</FormDescription>
 *       <FormMessage />
 *     </FormItem>
 *   )}
 * />
 */
function FormField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({ ...props }: FormFieldProps<TFieldValues, TName>) {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
}
FormField.displayName = 'FormField';

// ─── FormItem ─────────────────────────────────────────────────────────────────

export interface FormItemProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Additional CSS classes merged onto the root div. */
  className?: string;
  /** Child sub-components: FormLabel, FormControl, FormDescription, FormMessage. */
  children?: React.ReactNode;
}

/**
 * Wrapper that generates a stable `id` prefix and exposes it via
 * `FormItemContext` for `FormLabel` / `FormControl` / `FormDescription` /
 * `FormMessage` to consume.
 *
 * Each `FormItem` in the tree gets its own ID scope — avoids conflicts when
 * the same field name appears in a repeated group.
 */
const FormItem = React.forwardRef<HTMLDivElement, FormItemProps>(({ className, ...props }, ref) => {
  const id = React.useId();

  return (
    <FormItemContext.Provider value={{ id }}>
      <div ref={ref} className={cn('space-y-2', className)} {...props} />
    </FormItemContext.Provider>
  );
});
FormItem.displayName = 'FormItem';

// ─── FormLabel ────────────────────────────────────────────────────────────────

export interface FormLabelProps extends React.LabelHTMLAttributes<HTMLLabelElement> {
  /** Additional CSS classes merged onto the label. */
  className?: string;
  /** Label text content. */
  children?: React.ReactNode;
}

/**
 * Accessible label for the form field. Automatically wires `htmlFor` to the
 * control's `id`. Renders in `text-destructive` when the field has an error.
 *
 * Figma: text-sm font-medium — Shadcn Studio / Form / Label
 */
const FormLabel = React.forwardRef<HTMLLabelElement, FormLabelProps>(
  ({ className, ...props }, ref) => {
    const { error, formItemId } = useFormField();

    return (
      // biome-ignore lint/a11y/noLabelWithoutControl: htmlFor is set dynamically via formItemId from context
      <label
        ref={ref}
        htmlFor={formItemId}
        className={cn(
          // ── Typography — Figma: text-sm font-medium ──────────────────────────
          'text-sm font-medium leading-none',
          // ── Default text colour ───────────────────────────────────────────────
          'text-foreground',
          // ── Disabled peer — label dims when sibling input is disabled ─────────
          'peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
          // ── Error state — Figma: text-destructive ─────────────────────────────
          error && 'text-destructive',
          className,
        )}
        {...props}
      />
    );
  },
);
FormLabel.displayName = 'FormLabel';

// ─── FormControl ──────────────────────────────────────────────────────────────

export interface FormControlProps extends React.ComponentPropsWithoutRef<typeof Slot> {
  /** The form input to render — props are merged via Radix Slot. */
  children?: React.ReactNode;
}

/**
 * Merges accessibility attributes onto the child input via Radix `Slot`:
 * - `id` → matches `FormLabel`'s `htmlFor`
 * - `aria-describedby` → points to `FormDescription` and, on error, `FormMessage`
 * - `aria-invalid` → set to `true` when the field has an error
 *
 * The child must accept standard HTML input props (or equivalent).
 */
const FormControl = React.forwardRef<React.ElementRef<typeof Slot>, FormControlProps>(
  ({ ...props }, ref) => {
    const { error, formItemId, formDescriptionId, formMessageId } = useFormField();

    return (
      <Slot
        ref={ref}
        id={formItemId}
        aria-describedby={error ? `${formDescriptionId} ${formMessageId}` : formDescriptionId}
        aria-invalid={!!error}
        {...props}
      />
    );
  },
);
FormControl.displayName = 'FormControl';

// ─── FormDescription ──────────────────────────────────────────────────────────

export interface FormDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {
  /** Additional CSS classes merged onto the paragraph. */
  className?: string;
  /** Hint text content. */
  children?: React.ReactNode;
}

/**
 * Supplementary hint text rendered below the control. Wired into
 * `aria-describedby` on the control so screen readers announce it.
 *
 * Figma: text-xs text-muted-foreground — Shadcn Studio / Form / Description
 */
const FormDescription = React.forwardRef<HTMLParagraphElement, FormDescriptionProps>(
  ({ className, ...props }, ref) => {
    const { formDescriptionId } = useFormField();

    return (
      <p
        ref={ref}
        id={formDescriptionId}
        className={cn(
          // ── Typography — Figma: text-xs muted ────────────────────────────────
          'text-xs text-muted-foreground',
          className,
        )}
        {...props}
      />
    );
  },
);
FormDescription.displayName = 'FormDescription';

// ─── FormMessage ──────────────────────────────────────────────────────────────

export interface FormMessageProps extends React.HTMLAttributes<HTMLParagraphElement> {
  /** Additional CSS classes merged onto the paragraph. */
  className?: string;
  /**
   * Override message content.
   * When omitted, the error message from react-hook-form is displayed.
   * When both are absent the component renders nothing.
   */
  children?: React.ReactNode;
}

/**
 * Displays the field's validation error message from react-hook-form
 * `fieldState.error.message`, or custom `children` if provided.
 * Returns `null` when there is no error and no children.
 *
 * Announced live to screen readers via `role="alert"` + `aria-live="polite"`.
 *
 * Figma: text-xs font-medium text-destructive — Shadcn Studio / Form / Message
 */
const FormMessage = React.forwardRef<HTMLParagraphElement, FormMessageProps>(
  ({ className, children, ...props }, ref) => {
    const { error, formMessageId } = useFormField();
    const body = error ? String(error?.message ?? '') : children;

    if (!body) return null;

    return (
      <p
        ref={ref}
        id={formMessageId}
        role="alert"
        aria-live="polite"
        className={cn(
          // ── Typography — Figma: text-xs font-medium destructive ───────────────
          'text-xs font-medium text-destructive',
          className,
        )}
        {...props}
      >
        {body}
      </p>
    );
  },
);
FormMessage.displayName = 'FormMessage';

// ─── Exports ──────────────────────────────────────────────────────────────────

export {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
};
